type GuestSlug = 'ma' | 'james';
type SelectedChannel = 'text' | 'call';
type CallState = 'ready' | 'ringing' | 'connected' | 'ended';

interface GuestProfile {
	slug: GuestSlug;
	name: 'Ma' | 'James';
	textChannelName: string;
	callChannelName: string;
}

interface GuestMessage {
	id: string;
	sender: 'Mitchell' | 'Ma' | 'James';
	content: string;
	createdAt: string;
	read: boolean;
}

const GUEST_EXPERIENCE_BASE = '/people/test/guest-experience';

let selectedChannel: SelectedChannel = 'text';
let messages: GuestMessage[] = [];
let composerValue = '';
let settingsOpen = false;
let devBarOpen = true;
let mobileSidebarOpen = false;
let callState: CallState = 'ready';
let callMuted = false;
let speakerOn = true;
let connectedStartedAt = 0;
let connectedElapsedBefore = 0;
let endedDurationSeconds = 0;
let callTimerId: number | null = null;
let ringingTimerId: number | null = null;

export function isGuestExperiencePath(): boolean {
	const path = window.location.pathname;
	return path === `${GUEST_EXPERIENCE_BASE}/ma` || path === `${GUEST_EXPERIENCE_BASE}/james`;
}

export function renderGuestExperience(): void {
	mountGuestExperienceStyles();
	document.documentElement.removeAttribute('data-public-route');
	document.body.classList.add('guest-experience-body');
	document.body.removeAttribute('data-current-server-mirror');
	selectedChannel = 'text';
	composerValue = '';
	settingsOpen = false;
	devBarOpen = true;
	mobileSidebarOpen = false;
	callState = 'ready';
	callMuted = false;
	speakerOn = true;
	connectedStartedAt = 0;
	connectedElapsedBefore = 0;
	endedDurationSeconds = 0;
	clearCallTimers();
	messages = seedMessages(guestForRoute());
	paint();
}

function guestForRoute(): GuestProfile {
	const slug: GuestSlug = window.location.pathname.endsWith('/james') ? 'james' : 'ma';
	const name: GuestProfile['name'] = slug === 'james' ? 'James' : 'Ma';
	return {
		slug,
		name,
		textChannelName: `${name} text`,
		callChannelName: `${name} call`,
	};
}

function paint(): void {
	const app = document.getElementById('app');
	if (!app) return;

	const guest = guestForRoute();
	app.setAttribute('style', 'display:flex;width:100%;flex:1;min-height:0;overflow:hidden;flex-direction:column;');
	app.innerHTML = `
		${renderDevBar(guest)}
		<div class="ge-real-shell" style="display:flex;flex:1;min-height:0;overflow:hidden;">
			${renderServerRail()}
			${renderChannelList(guest)}
			${renderContentArea(guest)}
		</div>
		<div id="sidebar-scrim" class="${mobileSidebarOpen ? 'open' : ''}"></div>
		${settingsOpen ? renderSettingsModal(guest) : ''}
	`;
	wirePage();
}

function renderDevBar(guest: GuestProfile): string {
	return `
		<aside class="ge-devbar ${devBarOpen ? '' : 'ge-devbar-collapsed'}" aria-label="Mitchell testing controls">
			<button class="ge-devbar-toggle" id="ge-devbar-toggle" type="button" aria-expanded="${devBarOpen}" aria-label="Toggle testing controls">
				${iconChevronDown()}
			</button>
			${
				devBarOpen
					? `
						<div class="ge-devbar-copy">
							<strong>Test preview</strong>
							<span>Guest restrictions are active.</span>
							<code>${escapeHtml(window.location.pathname)}</code>
						</div>
						<div class="ge-devbar-actions" role="group" aria-label="Guest view selector">
							<button class="ge-segment ${guest.slug === 'ma' ? 'active' : ''}" type="button" data-guest-target="ma">Ma</button>
							<button class="ge-segment ${guest.slug === 'james' ? 'active' : ''}" type="button" data-guest-target="james">James</button>
						</div>
					`
					: ''
			}
		</aside>
	`;
}

function renderServerRail(): string {
	return `
		<nav id="server-rail" class="${mobileSidebarOpen ? 'open' : ''}" aria-label="Server rail">
			<div class="sr-icon-wrap">
				<div class="sr-icon active" data-tooltip="Mitchell Space" role="button" aria-label="Mitchell Space" tabindex="0">M</div>
			</div>
		</nav>
	`;
}

function renderChannelList(guest: GuestProfile): string {
	return `
		<div id="channel-list" class="${mobileSidebarOpen ? 'open cl-mobile-open' : ''}" aria-label="Mitchell Space channel list">
			<div class="cl-header">
				<span>Mitchell Space</span>
			</div>
			<div class="cl-channels" id="cl-channels">
				<div class="cl-category cl-cat" data-category="my-people" aria-expanded="true">
					<span class="cl-category-label">MY PEOPLE</span>
				</div>
				<div class="cl-category cl-category-sub cl-subcat" data-category="${escapeAttr(guest.name)}" aria-expanded="true">
					<span class="cl-category-label">${escapeHtml(guest.name)}</span>
				</div>
				<div class="cl-channel cl-channel-sub cl-ch ${selectedChannel === 'text' ? 'active' : ''}" data-channel="text" role="button" tabindex="0">
					<span class="cl-channel-icon cl-hash">#</span>
					<span class="cl-channel-name">${escapeHtml(guest.textChannelName)}</span>
				</div>
				<div class="cl-channel cl-channel-sub cl-ch cl-voice-channel ${selectedChannel === 'call' ? 'active' : ''}" data-channel="call" role="button" tabindex="0">
					<span class="cl-channel-icon cl-voice-icon">${iconSpeaker()}</span>
					<span class="cl-channel-name">${escapeHtml(guest.callChannelName)}</span>
				</div>
			</div>
			<div class="cl-user-bar cl-user-panel" role="region" aria-label="User profile bar">
				<div class="cl-user-avatar-wrap">
					<div class="cl-user-avatar">${escapeHtml(guest.name.charAt(0))}</div>
					<div class="cl-user-status-dot cl-status-active"></div>
				</div>
				<div class="cl-user-meta">
					<div class="cl-user-name">${escapeHtml(guest.name)}</div>
					<div class="cl-user-status cl-user-status-text">Guest</div>
				</div>
				<button class="cl-ctrl-btn ge-user-settings" id="ge-settings" type="button" aria-label="Guest settings" title="Guest settings">${iconSettings()}</button>
			</div>
		</div>
	`;
}

function renderContentArea(guest: GuestProfile): string {
	const channelName = selectedChannel === 'call' ? guest.callChannelName : guest.textChannelName;
	return `
		<div id="content-area" class="ca-wrap" aria-label="Content area">
			<div class="ca-header">
				<button class="ca-header-back" id="ge-content-menu" type="button" aria-label="Open channels">${iconMenu()}</button>
				<span class="ca-header-icon ${selectedChannel === 'text' ? 'cl-hash' : 'cl-voice-icon'}">
					${selectedChannel === 'text' ? '#' : iconSpeaker()}
				</span>
				<span class="ca-header-title">${escapeHtml(channelName)}</span>
				<span class="ca-topic-divider">|</span>
				<span class="ca-header-topic">${escapeHtml(topicForSelection(guest))}</span>
			</div>
			${selectedChannel === 'text' ? renderTextChannel(guest) : renderCallChannel()}
		</div>
	`;
}

function topicForSelection(guest: GuestProfile): string {
	if (selectedChannel === 'call') return 'Private guest call channel with Mitchell';
	return `Private guest text channel for ${guest.name}`;
}

function renderTextChannel(guest: GuestProfile): string {
	return `
		<div class="ca-body ca-body-chat chat-messages" id="ge-messages" aria-label="${escapeAttr(guest.textChannelName)}">
			<div class="ge-notices" aria-label="Guest channel notices">
				<div class="ge-notice ge-notice-info">${iconInfo()}<span>Messages will be erased 1 hour after read once backend retention is enabled.</span></div>
				<div class="ge-notice ge-notice-warn">${iconWarning()}<span>Screenshots are not permitted in this guest channel. App-level capture notices will show where detectable.</span></div>
				<div class="ge-notice ge-notice-info">${iconBell()}<span>Browser notifications will be enabled for new messages.</span></div>
			</div>
			${messages.map((message) => renderMessage(message)).join('')}
		</div>
		<form class="chat-compose ge-compose" id="ge-composer">
			<input class="chat-input chat-compose-input" id="ge-message-input" type="text" autocomplete="off" maxlength="2000" placeholder="Message # ${escapeAttr(guest.textChannelName)}" value="${escapeAttr(composerValue)}">
			<button class="chat-send-btn chat-compose-btn" type="submit" aria-label="Send message" data-filled="${composerValue.trim() ? '1' : '0'}">${iconSend()}</button>
		</form>
	`;
}

function renderMessage(message: GuestMessage): string {
	return `
		<article class="msg msg-row" data-message-id="${escapeAttr(message.id)}">
			<div class="msg-avatar">${escapeHtml(message.sender.charAt(0))}</div>
			<div class="msg-body">
				<div class="msg-header">
					<span class="msg-author">${escapeHtml(message.sender)}</span>
					<time class="msg-time">${escapeHtml(message.createdAt)}</time>
					<span class="ge-receipt" aria-label="${message.read ? 'Read' : 'Sent'}">${iconDoubleCheck()}</span>
				</div>
				<div class="msg-content msg-text">${escapeHtml(message.content)}</div>
			</div>
		</article>
	`;
}

function renderCallChannel(): string {
	return `
		<div class="ca-body ge-call" aria-label="Guest call channel">
			<div class="ge-call-stage">
				<div class="ge-call-avatar">M</div>
				<div class="ge-call-title">${escapeHtml(callStatusLabel())}</div>
				<div class="ge-call-detail">${escapeHtml(callStatusDetail())}</div>
				<div class="ge-call-labels">
					<span>${iconLock()}WebRTC encrypted media</span>
					<span>${iconShield()}Encrypted in transit</span>
				</div>
				<div class="ge-call-timer">${escapeHtml(formatSeconds(callDurationSeconds()))}</div>
			</div>
			<div class="ge-call-controls">
				${
					callState === 'connected'
						? `
							<button class="ge-call-control ${callMuted ? 'active' : ''}" id="ge-mute" type="button" aria-label="Mute" title="Mute">${iconMic()}</button>
							<button class="ge-call-control ${speakerOn ? 'active' : ''}" id="ge-speaker" type="button" aria-label="Speaker" title="Speaker">${iconVolume()}</button>
							<button class="ge-call-control ge-call-danger" id="ge-end-call" type="button" aria-label="End call" title="End call">${iconPhoneOff()}</button>
						`
						: `
							<button class="ge-call-start" id="ge-start-call" type="button">${iconPhone()}Call Mitchell</button>
						`
				}
			</div>
		</div>
	`;
}

function renderSettingsModal(guest: GuestProfile): string {
	return `
		<div class="ge-modal-backdrop" role="presentation">
			<section class="ge-modal" role="dialog" aria-modal="true" aria-labelledby="ge-settings-title">
				<header class="ge-modal-header">
					<h2 id="ge-settings-title">Guest settings</h2>
					<button class="ge-modal-close" id="ge-close-settings" type="button" aria-label="Close settings">${iconX()}</button>
				</header>
				<dl class="ge-settings-list">
					<div><dt>Account type</dt><dd>Guest</dd></div>
					<div><dt>Display name</dt><dd>${escapeHtml(guest.name)}</dd></div>
					<div><dt>Server</dt><dd>Mitchell Space</dd></div>
					<div><dt>Category access</dt><dd>My People / ${escapeHtml(guest.name)}</dd></div>
				</dl>
				<button class="ge-settings-danger" type="button" disabled>Delete my guest account</button>
				<p class="ge-settings-note">Account deletion will be available once backend is deployed.</p>
				<button class="ge-settings-signout" id="ge-sign-out" type="button">${iconLogOut()}Sign out</button>
			</section>
		</div>
	`;
}

function wirePage(): void {
	document.getElementById('ge-devbar-toggle')?.addEventListener('click', () => {
		devBarOpen = !devBarOpen;
		paint();
	});

	document.querySelectorAll<HTMLButtonElement>('[data-guest-target]').forEach((button) => {
		button.addEventListener('click', () => {
			const slug: GuestSlug = button.dataset.guestTarget === 'james' ? 'james' : 'ma';
			window.history.pushState({}, '', `${GUEST_EXPERIENCE_BASE}/${slug}`);
			messages = seedMessages(guestForRoute());
			selectedChannel = 'text';
			composerValue = '';
			mobileSidebarOpen = false;
			settingsOpen = false;
			resetCall();
			paint();
		});
	});

	document.querySelectorAll<HTMLElement>('[data-channel]').forEach((element) => {
		element.addEventListener('click', () => {
			selectedChannel = element.dataset.channel === 'call' ? 'call' : 'text';
			mobileSidebarOpen = false;
			paint();
			if (selectedChannel === 'text') scrollMessagesToBottom();
		});
		element.addEventListener('keydown', (event) => {
			if (event.key !== 'Enter' && event.key !== ' ') return;
			event.preventDefault();
			element.click();
		});
	});

	document.getElementById('sidebar-scrim')?.addEventListener('click', () => {
		mobileSidebarOpen = false;
		paint();
	});
	document.getElementById('ge-content-menu')?.addEventListener('click', toggleMobileSidebar);
	document.getElementById('ge-settings')?.addEventListener('click', () => {
		settingsOpen = true;
		paint();
	});
	document.getElementById('ge-close-settings')?.addEventListener('click', () => {
		settingsOpen = false;
		paint();
	});
	document.getElementById('ge-sign-out')?.addEventListener('click', () => {
		settingsOpen = false;
		selectedChannel = 'text';
		paint();
	});
	document.getElementById('ge-composer')?.addEventListener('submit', (event) => {
		event.preventDefault();
		const input = document.getElementById('ge-message-input');
		const value = input instanceof HTMLInputElement ? input.value.trim() : '';
		if (!value) return;
		const guest = guestForRoute();
		messages = messages.concat({
			id: `local-${Date.now()}`,
			sender: guest.name,
			content: value,
			createdAt: 'Just now',
			read: true,
		});
		composerValue = '';
		paint();
		scrollMessagesToBottom();
	});
	document.getElementById('ge-message-input')?.addEventListener('input', (event) => {
		composerValue = event.target instanceof HTMLInputElement ? event.target.value : '';
	});
	document.getElementById('ge-start-call')?.addEventListener('click', startCall);
	document.getElementById('ge-mute')?.addEventListener('click', () => {
		callMuted = !callMuted;
		paint();
	});
	document.getElementById('ge-speaker')?.addEventListener('click', () => {
		speakerOn = !speakerOn;
		paint();
	});
	document.getElementById('ge-end-call')?.addEventListener('click', endCall);
}

function toggleMobileSidebar(): void {
	mobileSidebarOpen = !mobileSidebarOpen;
	paint();
}

function seedMessages(guest: GuestProfile): GuestMessage[] {
	return [
		{
			id: 'm-1',
			sender: 'Mitchell',
			content: `Hey ${guest.name}, welcome to your space. Feel free to message anytime.`,
			createdAt: 'Today 9:10 AM',
			read: true,
		},
		{
			id: 'm-2',
			sender: guest.name,
			content: 'Thanks Mitchell, this is great.',
			createdAt: 'Today 9:12 AM',
			read: true,
		},
		{
			id: 'm-3',
			sender: 'Mitchell',
			content: 'Let me know if you need anything.',
			createdAt: 'Today 9:14 AM',
			read: true,
		},
	];
}

function startCall(): void {
	clearCallTimers();
	callState = 'ringing';
	endedDurationSeconds = 0;
	paint();
	ringingTimerId = window.setTimeout(() => {
		callState = 'connected';
		connectedStartedAt = Date.now();
		connectedElapsedBefore = 0;
		paint();
		callTimerId = window.setInterval(paint, 1000);
	}, 1200);
}

function endCall(): void {
	endedDurationSeconds = callDurationSeconds();
	clearCallTimers();
	callState = 'ended';
	connectedStartedAt = 0;
	connectedElapsedBefore = 0;
	paint();
}

function resetCall(): void {
	clearCallTimers();
	callState = 'ready';
	callMuted = false;
	speakerOn = true;
	connectedStartedAt = 0;
	connectedElapsedBefore = 0;
	endedDurationSeconds = 0;
}

function clearCallTimers(): void {
	if (callTimerId !== null) window.clearInterval(callTimerId);
	if (ringingTimerId !== null) window.clearTimeout(ringingTimerId);
	callTimerId = null;
	ringingTimerId = null;
}

function callDurationSeconds(): number {
	if (callState === 'connected' && connectedStartedAt > 0) {
		return connectedElapsedBefore + Math.floor((Date.now() - connectedStartedAt) / 1000);
	}
	if (callState === 'ended') return endedDurationSeconds;
	return 0;
}

function callStatusLabel(): string {
	if (callState === 'ringing') return 'Ringing Mitchell';
	if (callState === 'connected') return 'Connected with Mitchell';
	if (callState === 'ended') return 'Call ended';
	return 'Ready to call Mitchell';
}

function callStatusDetail(): string {
	if (callState === 'ringing') return 'Waiting for Mitchell to answer.';
	if (callState === 'connected') return 'Private guest call is active.';
	if (callState === 'ended') return 'You can call Mitchell again when ready.';
	return 'Start a private WebRTC call from this guest channel.';
}

function formatSeconds(totalSeconds: number): string {
	const minutes = Math.floor(totalSeconds / 60)
		.toString()
		.padStart(2, '0');
	const seconds = (totalSeconds % 60).toString().padStart(2, '0');
	return `${minutes}:${seconds}`;
}

function scrollMessagesToBottom(): void {
	window.requestAnimationFrame(() => {
		const el = document.getElementById('ge-messages');
		if (el) el.scrollTop = el.scrollHeight;
	});
}

function mountGuestExperienceStyles(): void {
	if (document.getElementById('guest-experience-styles')) return;
	const style = document.createElement('style');
	style.id = 'guest-experience-styles';
	style.textContent = `
.guest-experience-body { margin: 0; overflow: hidden; background: var(--bg-tertiary, var(--surface-0)); color: var(--text-primary); }
.ge-devbar { min-height: 40px; display: flex; align-items: center; gap: var(--space-3); padding: var(--space-1) var(--space-3); background: var(--surface-1); border-bottom: 1px solid var(--border); box-sizing: border-box; flex-shrink: 0; }
.ge-devbar-collapsed { min-height: 32px; }
.ge-devbar-toggle { width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center; border: 0; border-radius: 4px; background: transparent; color: var(--fg-muted); cursor: pointer; }
.ge-devbar-toggle:hover { background: var(--surface-1-hover); color: var(--fg); }
.ge-devbar-collapsed .ge-devbar-toggle svg { transform: rotate(-90deg); }
.ge-devbar-copy { min-width: 0; display: flex; align-items: center; gap: var(--space-3); color: var(--fg-muted); font-size: var(--text-12); }
.ge-devbar-copy strong { color: var(--fg-bright); font-size: var(--text-13); }
.ge-devbar-copy code { color: var(--fg-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ge-devbar-actions { margin-left: auto; display: flex; align-items: center; gap: 1px; background: var(--surface-0); border-radius: 4px; padding: 2px; }
.ge-segment { min-width: 56px; height: 28px; border: 0; border-radius: 3px; background: transparent; color: var(--fg-muted); cursor: pointer; font-weight: 600; }
.ge-segment.active { background: var(--accent); color: var(--fg-on-accent); }
.ge-real-shell { background: var(--bg); }
.cl-user-meta { min-width: 0; flex: 1; }
.ge-user-settings { flex-shrink: 0; }
.ge-notices { display: flex; flex-direction: column; gap: 6px; padding: 8px 0 16px; }
.ge-notice { display: flex; align-items: flex-start; gap: 10px; padding: 10px 16px; border-radius: var(--radius-md); font-size: var(--text-13); line-height: 1.5; }
.ge-notice-info { background: var(--info-soft); color: var(--text-secondary); }
.ge-notice-warn { background: var(--warning-soft); color: var(--text-secondary); }
.ge-notice svg { width: 18px; height: 18px; flex-shrink: 0; }
.ge-receipt { color: var(--fg-muted); display: inline-flex; flex-shrink: 0; }
.ge-receipt svg { width: 15px; height: 15px; }
.ge-call { display: flex; flex-direction: column; padding: 0; }
.ge-call-stage { flex: 1; min-height: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: var(--space-3); padding: var(--space-6); text-align: center; }
.ge-call-avatar { width: 96px; height: 96px; border-radius: 50%; background: var(--accent); color: var(--fg-on-accent); display: flex; align-items: center; justify-content: center; font-size: var(--text-28); font-weight: 700; }
.ge-call-title { color: var(--fg-bright); font-size: var(--text-24); font-weight: 700; }
.ge-call-detail { color: var(--fg-muted); font-size: var(--text-14); }
.ge-call-labels { display: flex; flex-wrap: wrap; justify-content: center; gap: var(--space-2); }
.ge-call-labels span { display: inline-flex; align-items: center; gap: 6px; min-height: 28px; padding: 0 var(--space-3); border-radius: 4px; background: var(--surface-1); color: var(--fg-muted); font-size: var(--text-12); }
.ge-call-labels svg { width: 15px; height: 15px; color: var(--accent); }
.ge-call-timer { color: var(--fg-muted); font-size: var(--text-24); font-weight: 700; font-variant-numeric: tabular-nums; }
.ge-call-controls { min-height: 88px; display: flex; align-items: center; justify-content: center; gap: var(--space-3); padding: var(--space-4); border-top: 1px solid var(--border); box-sizing: border-box; }
.ge-call-start, .ge-call-control { border: 0; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; color: var(--fg-bright); }
.ge-call-start { min-height: 44px; gap: var(--space-2); padding: 0 var(--space-5); border-radius: 4px; background: var(--status-online, var(--ok)); font-weight: 700; }
.ge-call-control { width: 52px; height: 52px; border-radius: 50%; background: var(--surface-1); color: var(--fg); }
.ge-call-control.active { background: var(--accent); color: var(--fg-on-accent); }
.ge-call-danger { background: var(--err, var(--danger)); color: var(--fg-on-accent); }
.ge-call-start svg, .ge-call-control svg { width: 20px; height: 20px; }
.ge-modal-backdrop { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; padding: var(--space-4); background: var(--overlay-scrim); z-index: var(--z-tooltip); box-sizing: border-box; }
.ge-modal { width: min(100%, 440px); display: flex; flex-direction: column; gap: var(--space-4); padding: var(--space-5); border-radius: 8px; background: var(--bg-floating, var(--surface-1)); color: var(--fg); box-shadow: var(--shadow-3); }
.ge-modal-header { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); }
.ge-modal h2 { margin: 0; color: var(--fg-bright); font-size: var(--text-20); }
.ge-modal-close { width: 32px; height: 32px; border: 0; border-radius: 4px; background: transparent; color: var(--fg-muted); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.ge-modal-close:hover { background: var(--surface-1-hover); color: var(--fg); }
.ge-settings-list { display: flex; flex-direction: column; gap: var(--space-2); margin: 0; }
.ge-settings-list div { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); padding-bottom: var(--space-2); border-bottom: 1px solid var(--border); }
.ge-settings-list dt { color: var(--fg-muted); font-size: var(--text-13); }
.ge-settings-list dd { margin: 0; color: var(--fg-bright); font-size: var(--text-13); text-align: right; }
.ge-settings-danger, .ge-settings-signout { min-height: 38px; border: 0; border-radius: 4px; display: inline-flex; align-items: center; justify-content: center; gap: var(--space-2); font-weight: 700; }
.ge-settings-danger { background: var(--danger-soft, var(--surface-1)); color: var(--fg-muted); cursor: not-allowed; }
.ge-settings-note { margin: calc(var(--space-2) * -1) 0 0; color: var(--fg-muted); font-size: var(--text-12); }
.ge-settings-signout { background: var(--surface-1); color: var(--fg); cursor: pointer; }
.ge-settings-signout:hover { background: var(--surface-1-hover); }
#app svg { display: block; stroke: currentColor; }
@media (max-width: 768px) {
	.ge-devbar { align-items: flex-start; }
	.ge-devbar-copy { flex-direction: column; align-items: flex-start; gap: 2px; }
	.ge-devbar-actions { margin-left: 0; }
}
`;
	document.head.appendChild(style);
}

function svg(path: string): string {
	return `<svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`;
}

function iconSpeaker(): string {
	return svg('<path d="M11 5 6 9H2v6h4l5 4V5z"></path><path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>');
}

function iconPhone(): string {
	return svg(
		'<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.11 4.2 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.91.33 1.8.62 2.65a2 2 0 0 1-.45 2.11L8.1 9.9a16 16 0 0 0 6 6l1.42-1.18a2 2 0 0 1 2.11-.45c.85.29 1.74.5 2.65.62A2 2 0 0 1 22 16.92z"></path>',
	);
}

function iconPhoneOff(): string {
	return svg(
		'<path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.42-1.18a2 2 0 0 1 2.11-.45c.85.29 1.74.5 2.65.62A2 2 0 0 1 22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07"></path><path d="M8.6 8.6 7.32 7.32a2 2 0 0 1-.45-2.11c.29-.85.5-1.74.62-2.65A2 2 0 0 0 5.5 1H4.11a2 2 0 0 0-2 2.2 19.8 19.8 0 0 0 3.07 8.63"></path><path d="M2 2l20 20"></path>',
	);
}

function iconSettings(): string {
	return svg(
		'<path d="M12 15.5A3.5 3.5 0 1 0 12 8a3.5 3.5 0 0 0 0 7.5z"></path><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.05.05a2 2 0 1 1-2.83 2.83l-.05-.05A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .2 1.7 1.7 0 0 0-1 1.55V21a2 2 0 1 1-4 0v-.08a1.7 1.7 0 0 0-1-1.55 1.7 1.7 0 0 0-1-.2 1.7 1.7 0 0 0-1.88.34l-.05.05a2 2 0 1 1-2.83-2.83l.05-.05A1.7 1.7 0 0 0 3.6 15a1.7 1.7 0 0 0-.2-1 1.7 1.7 0 0 0-1.55-1H2a2 2 0 1 1 0-4h.08a1.7 1.7 0 0 0 1.55-1 1.7 1.7 0 0 0 .2-1 1.7 1.7 0 0 0-.34-1.88l-.05-.05a2 2 0 1 1 2.83-2.83l.05.05A1.7 1.7 0 0 0 8 3.6a1.7 1.7 0 0 0 1-.2A1.7 1.7 0 0 0 10 1.85V2a2 2 0 1 1 4 0v.08a1.7 1.7 0 0 0 1 1.55 1.7 1.7 0 0 0 1 .2 1.7 1.7 0 0 0 1.88-.34l.05-.05a2 2 0 1 1 2.83 2.83l-.05.05A1.7 1.7 0 0 0 19.4 8c.1.33.17.66.2 1H20a2 2 0 1 1 0 4h-.08a1.7 1.7 0 0 0-1.55 1z"></path>',
	);
}

function iconInfo(): string {
	return svg('<circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4"></path><path d="M12 8h.01"></path>');
}

function iconWarning(): string {
	return svg(
		'<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><path d="M12 9v4"></path><path d="M12 17h.01"></path>',
	);
}

function iconBell(): string {
	return svg(
		'<path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 7h18s-3 0-3-7"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path>',
	);
}

function iconSend(): string {
	return svg('<path d="M22 2 11 13"></path><path d="M22 2 15 22l-4-9-9-4 20-7z"></path>');
}

function iconDoubleCheck(): string {
	return svg('<path d="m3 12 3 3 6-6"></path><path d="m11 12 3 3 7-8"></path>');
}

function iconLock(): string {
	return svg('<rect x="3" y="11" width="18" height="11" rx="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path>');
}

function iconShield(): string {
	return svg('<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>');
}

function iconMic(): string {
	return svg(
		'<path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><path d="M12 19v4"></path><path d="M8 23h8"></path>',
	);
}

function iconVolume(): string {
	return svg(
		'<path d="M11 5 6 9H2v6h4l5 4V5z"></path><path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path><path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>',
	);
}

function iconX(): string {
	return svg('<path d="M18 6 6 18"></path><path d="m6 6 12 12"></path>');
}

function iconLogOut(): string {
	return svg(
		'<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><path d="M16 17l5-5-5-5"></path><path d="M21 12H9"></path>',
	);
}

function iconMenu(): string {
	return svg('<path d="M3 6h18"></path><path d="M3 12h18"></path><path d="M3 18h18"></path>');
}

function iconChevronDown(): string {
	return svg('<path d="m6 9 6 6 6-6"></path>');
}

function escapeHtml(value: string): string {
	return value.replace(/[&<>"']/g, (char) => {
		if (char === '&') return '&amp;';
		if (char === '<') return '&lt;';
		if (char === '>') return '&gt;';
		if (char === '"') return '&quot;';
		return '&#39;';
	});
}

function escapeAttr(value: string): string {
	return escapeHtml(value);
}
